import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction, action, untracked } from "mobx";

import Exercise from "./Exercise";
import Serie from "./Serie";
import Attribute from "./Attribute";
import { ProtocolTitles } from "../../Resources/Constants";

class Workout {
  exercisesToAdd = [];
  showingEditWorkout = false;
  auxDate = null;
  showingCoachWorkouts = false;

  constructor(
    id = uuidv4(),
    date = null,
    name = "",
    order = 0,
    week = 1,
    isSelected = false,
    block = null,
    exercises = [],
    coach = null,
    notes = ""
  ) {
    this.id = id;
    this.date = date;
    this.name = name;
    this.order = order;
    this.week = week;
    this.exercises = exercises;
    this.isSelected = isSelected;
    this.block = block;
    this.exercises = exercises;
    this.coach = coach;
    this.notes = notes;
    this.hasChanged = false;

    makeAutoObservable(this);

    reaction(
      () => ({
        id: this.id,
        date: this.date,
        name: this.name,
        notes: this.notes,
        order: this.order,
        week: this.week,
        exercises: this.exercises,
      }),
      () => {
        console.log("Reaction Workout");

        if (this.block?.client && !this.block.client.avoidTransaction) {
          this.hasChanged = true;
          this.block.client.updateClientTransactionData(this.block.id);
          this.block.client.startOrResetBatchTimer();
        } else if (this.block?.coach) {
          this.block.coach.startOrResetBatchTimer();
          this.block.coach.updateCoachTransactionBlocks(
            this.block.id.toLowerCase()
          );
        } else if (this.coach) {
          this.hasChanged = true;
          this.coach.updateCoachTransactionWorkouts(this.id);
          this.coach.startOrResetBatchTimer();
        }
      }
    );
  }

  // #region /* WORKOUT LIBRARY */
  copyToLibrary(showToast) {
    const coach = this.block?.client?.coach ?? this.block?.coach;
    if (coach) {
      const workoutLibrary = coach.workoutLibrary ?? [];
      const newOrder =
        workoutLibrary.length > 0
          ? Math.max(...workoutLibrary.map((w) => w.order)) + 1
          : 1;
      const newWorkout = new Workout(
        uuidv4(),
        null,
        this.name,
        newOrder,
        0,
        false,
        null,
        [],
        coach
      );
      this.exercises &&
        this.exercises.forEach((exercise) => {
          const newExercise = new Exercise(
            uuidv4(),
            exercise.name,
            exercise.originalName,
            exercise.notes,
            exercise.order,
            true,
            false,
            newWorkout,
            [],
            [],
            undefined,
            undefined,
            exercise.link
          );
          exercise.series &&
            exercise.series.forEach((serie) => {
              newExercise.copySerie(serie);
            });
          exercise.attributes &&
            exercise.attributes.forEach((attribute) => {
              newExercise.copyAttribute(attribute);
            });
          newWorkout.exercises.push(newExercise);
        });
      if (!coach.workoutLibrary) {
        coach.workoutLibrary = [];
      }
      coach.workoutLibrary.push(newWorkout);
      if (newWorkout.coach) {
        newWorkout.coach.updateCoachTransactionWorkouts(newWorkout.id);
        newWorkout.coach.startOrResetBatchTimer();
      }

      showToast("workoutExportedToLibrary-string", {
        type: "info",
        description: "workoutExportedToLibraryDescription-string",
        variables: {
          workoutName: this.name,
        },
      });
    }
  }

  addLibraryWorkout(workout) {
    //FUNC TO ADD THE EXERCISES FROM A WORKOUT OF THE LIBRARY TO ANOTHER WORKOUT
    //THIS IS A WORKOUT FROM THE LIBRARY
    this.exercises
      .sort((a, b) => a.order - b.order)
      .forEach((exercise) => {
        const newOrder =
          workout.exercisesToAdd.length > 0
            ? Math.max(
                ...workout.exercisesToAdd.map((exercise) => exercise.order)
              ) + 1
            : 0;
        const newExercise = new Exercise(
          uuidv4(),
          exercise.name,
          exercise.originalName,
          exercise.notes,
          newOrder,
          true,
          false,
          workout,
          [],
          [],
          uuidv4(),
          undefined,
          exercise.link
        );
        exercise.series &&
          exercise.series.forEach((serie) => {
            const newSerie = new Serie(
              uuidv4(),
              serie.order,
              serie.reps,
              serie.rpe,
              serie.weight,
              serie.targetReps,
              serie.targetRpe,
              serie.targetWeight,
              newExercise,
              serie.relevance,
              undefined,
              serie.requireVideo
            );
            newExercise.series.push(newSerie);
          });
        exercise.attributes &&
          exercise.attributes.forEach((attribute) => {
            const newAttribute = new Attribute(
              uuidv4(),
              attribute.name,
              attribute.title,
              attribute.subtitle,
              attribute.order,
              false,
              attribute.isSelected,
              null,
              newExercise
            );
            newExercise.attributes.push(newAttribute);
          });
        workout.exercisesToAdd.push(newExercise);
      });
  }
  // #endregion

  // #region /* ADD EXERCISES */
  get selectedExerciseToAdd() {
    return this.exercisesToAdd.find((exercise) => exercise.isSelected === true);
  }
  setExercisesToAdd(newArray) {
    if (newArray) {
      // this.exercisesToAdd = newArray;
      this.exercisesToAdd = [...newArray];
    } else {
      this.exercisesToAdd = [];
      this.exercises.map((exercise) => exercise.duplicateExercise());
    }
  }

  setExercises() {
    // Identify exercises that were deleted
    const exercisesToAddIds = this.exercisesToAdd.map((exercise) =>
      exercise.exerciseToAddId.toString()
    );
    // console.log("exercisesToAddIds: ", exercisesToAddIds);
    const exercisesToDelete = this.exercises.filter(
      (exercise) => !exercisesToAddIds.includes(exercise.id.toString())
    );

    // Before deleting the exercises, call copyExerciseToBlockProtocols for each
    exercisesToDelete.forEach((e) => e.copyExerciseToBlockProtocols());

    this.exercises = this.exercises.filter(
      (exercise) => !exercisesToDelete.includes(exercise)
    );

    this.exercisesToAdd.forEach((exerciseToAdd) => {
      exerciseToAdd.duplicateExerciseToAdd(this);
    });

    // After changes are applied, you might want to clear exercisesToAdd or repopulate it for further edits
    this.exercisesToAdd = [];

    // Continue with your existing logic to update client data and reset timer
    if (this.block && this.block.client) {
      this.block.client.updateClientTransactionData(this.block.id);
      this.block.client.startOrResetBatchTimer();
    }
  }

  setSelectedExercise(selectedExercise) {
    this.exercises.forEach((exercise) => {
      if (exercise.id === selectedExercise.id) {
        exercise.setIsSelected(true);
      } else {
        exercise.setIsSelected(false);
      }
    });
  }

  setSelectedExerciseToAdd(selectedExercise) {
    this.exercisesToAdd.forEach((exercise) => {
      if (exercise.id === selectedExercise.id) {
        exercise.setIsSelected(true);
      } else {
        exercise.setIsSelected(false);
      }
    });
  }

  moveExercise(exercise, direction) {
    // First, sort the exercises by their order to ensure they are in the correct initial order
    const exercisesArray = this.exercisesToAdd.sort(
      (a, b) => a.order - b.order
    );

    // Find the index of the exercise in the sorted array
    const index = exercisesArray.findIndex((ex) => ex.id === exercise.id);
    if (index === -1) return; // Exit if the exercise is not found

    // Determine the new index based on the direction ('up' moves the item earlier, 'down' moves it later)
    const newIndex = direction === "up" ? index - 1 : index + 1;

    // Check if the new index is within bounds
    if (newIndex >= 0 && newIndex < exercisesArray.length) {
      // Swap the exercises in the array
      [exercisesArray[index], exercisesArray[newIndex]] = [
        exercisesArray[newIndex],
        exercisesArray[index],
      ];

      // Update the order of the exercises based on their new positions
      exercisesArray.forEach((ex, idx) => {
        ex.order = idx;
      });
    }
  }

  // addNewSlotExerciseToWorkout(slotExercise) {
  //   this.copyExercise(slotExercise);
  // }
  // #endregion

  // #region /* EXERCISE ACTIONS */
  get exercisesShown() {
    return this.exercises.every((exercise) => exercise.isShown === true);
  }

  showOrFoldAllExercises() {
    if (this.exercisesShown) {
      this.exercises.forEach((exercise) => {
        exercise.isShown = false;
      });
    } else {
      this.exercises.forEach((exercise) => {
        exercise.isShown = true;
      });
    }
  }
  get selectedExercise() {
    return this.exercises.find((exercise) => exercise.isSelected === true);
  }
  // #endregion

  // #region /* REAL-TIME & COPY */
  fetchExercise(exercise) {
    const newExercise = new Exercise(
      String(exercise.id),
      exercise.name,
      exercise.original_name,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      this,
      [],
      [],
      undefined,
      undefined,
      exercise.link
    );
    if (exercise.order === 0) {
      newExercise.setProperty("isSelected", true);
    }

    newExercise.isSecondaryProtocol = exercise.is_secondary_protocol ?? false;

    newExercise.protocolText = exercise.protocol_text ?? ""; //Allow exercises to have a protocolText when they have been temporarily unlinked from slot

    if (exercise.protocol_slot !== undefined) {
      newExercise.protocolSlot = exercise.protocol_slot ?? null;
      newExercise.protocolWeek = exercise.protocol_week ?? null;
    } else {
      if (exercise.squat_slot !== undefined) {
        // newExercise.setProperty("squatSlot", exercise.squat_slot);
        if (exercise.squat_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_SQUAT;
        } else if (exercise.squat_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_SQUAT;
        } else if (exercise.squat_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_SQUAT;
        } else if (exercise.squat_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_SQUAT;
        } else if (exercise.squat_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_SQUAT;
        }
      }
      if (exercise.bench_press_slot !== undefined) {
        // newExercise.setProperty("benchPressSlot", exercise.bench_press_slot);
        if (exercise.bench_press_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_BENCH_PRESS;
        }
      }
      if (exercise.deadlift_slot !== undefined) {
        // newExercise.setProperty("deadliftSlot", exercise.deadlift_slot);
        if (exercise.deadlift_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_DEADLIFT;
        }
      }
    }
    exercise?.series.forEach((serie) => {
      newExercise.fetchSerie(serie);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.fetchAttribute(attribute);
    });
    this.exercises.push(newExercise);
  }

  updateWorkout(workout) {
    this.date = workout.date?.toDate() ?? null;
    this.name = workout.name ?? "";
    this.order = workout.order ?? 0;
    this.week = workout.week ?? 0;
  }

  uploadToCloud(timestamp = null) {
    let exercisesArray = this.exercises.map((exercise) =>
      exercise.uploadToCloud()
    );

    return {
      id: this.id.toString().toLowerCase(),
      date: this.date,
      name: this.name,
      order: this.order,
      week: this.week,
      exercises: exercisesArray,
      ...(timestamp && { last_update: timestamp }),
      ...(this.notes != "" && { notes: this.notes }),
      device_os: "web",
    };
  }
  copyExercise(exercise, keepActual = false) {
    const newExercise = new Exercise(
      undefined,
      exercise.name,
      exercise.originalName,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      this,
      [],
      [],
      undefined,
      undefined,
      exercise.link
    );
    if (exercise.order === 0) {
      newExercise.setProperty("isSelected", true);
    }

    newExercise.isSecondaryProtocol = exercise.isSecondaryProtocol ?? null;

    if (exercise.protocolSlot) {
      newExercise.protocolSlot = exercise.protocolSlot ?? null;
      newExercise.protocolWeek = exercise.protocolWeek ?? null;
      newExercise.protocolText = exercise.protocolText ?? "";
    }
    exercise?.series.forEach((serie) => {
      newExercise.copySerie(serie, keepActual);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.copyAttribute(attribute);
    });
    this.exercises.push(newExercise);

    return newExercise;
  }
  // #endregion

  // #region /* HELPERS */
  get allWorkoutSeries() {
    const result = this.exercises.reduce((acc, exercise) => {
      exercise.series.forEach((serie) => {
        acc.push(serie);
      });
      return acc;
    }, []);

    return result;
  }

  setIsSelected(value) {
    this.isSelected = value;
  }

  // deleteWorkout() {
  //   // First, copy each exercise to block protocols
  //   this.exercises.forEach(e => e.copyExerciseToBlockProtocols());

  //   // Then remove this workout from its parent block
  //   if (this.block) {
  //     const idx = this.block.workouts.findIndex(w => w.id === this.id);
  //     if (idx > -1) {
  //       this.block.workouts.splice(idx, 1);
  //     }
  //   }else if (this.coach){
  //      const idx = this.coach.workoutLibrary.findIndex(w => w.id === this.id);
  //     if (idx > -1) {
  //       this.coach.workoutLibrary.splice(idx, 1);
  //     }
  //   }

  //    // Persist changes depending on client or coach context
  //     // if (this.block.client) {
  //     //   this.block.client.updateClientTransactionData(this.block.id);
  //     //   this.block.client.startOrResetBatchTimer();
  //     // } else if (this.block.coach) {
  //     //   this.block.coach.updateCoachTransactionBlocks(this.block.id.toLowerCase());
  //     //   this.block.coach.startOrResetBatchTimer();
  //     // }
  // }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
  // #endregion
}
export default Workout;
